import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * PairSelector — Compact pair picker used on Home + Trade screens.
 * Categories match backend support from PremiumFeatures.jsx (legacy multi-pair UI).
 */
export const PAIR_CATEGORIES = {
  gold: { label: 'Gold', pairs: ['XAU/USD'] },
  forex: { label: 'Forex Major', pairs: ['EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/USD'] },
  crosses: { label: 'Crosses', pairs: ['GBP/JPY', 'EUR/JPY', 'EUR/GBP'] },
  crypto: { label: 'Crypto', pairs: ['BTC/USD', 'ETH/USD'] },
};

export const ALL_PAIRS = Object.values(PAIR_CATEGORIES).flatMap((c) => c.pairs);

const PairSelector = ({ value, onChange, disabled = false, compact = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const handlePick = (pair) => {
    onChange?.(pair);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        data-testid="pair-selector-button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: compact ? '6px 10px' : '8px 14px',
          borderRadius: '12px',
          border: '1px solid #E2E8F0',
          background: '#FFF',
          fontSize: compact ? '12px' : '13px',
          fontWeight: '700',
          color: '#0F172A',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          fontFamily: 'Poppins, sans-serif',
          letterSpacing: '0.02em',
        }}
      >
        {value || 'Select Pair'}
        <ChevronDown size={14} color="#64748B" />
      </button>

      {open && (
        <div
          data-testid="pair-selector-dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '6px',
            width: '240px',
            maxHeight: '360px',
            overflowY: 'auto',
            background: '#FFF',
            borderRadius: '14px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 12px 32px rgba(15,23,42,0.10)',
            zIndex: 20,
            padding: '8px',
          }}
        >
          {Object.entries(PAIR_CATEGORIES).map(([catKey, cat]) => (
            <div key={catKey} style={{ marginBottom: '8px' }}>
              <div style={{
                fontSize: '10px', fontWeight: '700', color: '#94A3B8',
                textTransform: 'uppercase', letterSpacing: '0.1em',
                padding: '6px 8px 4px',
              }}>
                {cat.label}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '0 4px 4px' }}>
                {cat.pairs.map((p) => {
                  const selected = p === value;
                  return (
                    <button
                      key={p}
                      data-testid={`pair-option-${p.replace('/', '-')}`}
                      onClick={() => handlePick(p)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '10px',
                        border: `1px solid ${selected ? '#F5A623' : '#E2E8F0'}`,
                        background: selected ? 'linear-gradient(135deg, #F5A623, #E8951A)' : '#F8FAFC',
                        color: selected ? '#FFF' : '#0F172A',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PairSelector;
